import { SlidersHorizontal } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Routes } from '@/routes/routes';
import { AnalyticsKPIs } from './components/AnalyticsKPIs';
import { BarChart } from './components/BarChart';
import {
	ConnEditDialog,
	ConnTestDialog,
	ConnViewDialog,
} from './components/ConnectionDialogs';
import { ConnectionForm } from './components/ConnectionForm';
import { ConnectionSummary } from './components/ConnectionSummary';
import { ConnectionsTable } from './components/ConnectionsTable';
import { ProviderSelector } from './components/ProviderSelector';
import { RecentActivity } from './components/RecentActivity';
import { RouteHealthTable } from './components/RouteHealthTable';
import { RoutingCards } from './components/RoutingCards';
import { RoutingDetailDialog } from './components/RoutingDetailDialog';
import { RoutingSetupDialog } from './components/RoutingSetupDialog';
import { RoutingTable } from './components/RoutingTable';
import {
	AGGREGATE_SEED,
	BAR_CHART_SEED,
	RECENT_ACTIVITY,
	ROUTE_HEALTH,
} from './data/analytics';
import { CLIENT_DATA } from './data/clients';
import { CONNECTIONS } from './data/connections';
import { ROUTING_DATA } from './data/routing';
import type { Connection, Provider, RoutingConfig } from './types';

type Tab = 'analytics' | 'connections' | 'routing';
type TimeWindow = '24h' | '48h' | '7d' | '30d';
type Channel = 'Email' | 'Push' | 'Sms' | 'All';

type FlowStep =
	| { step: 'idle' }
	| { step: 'provider' }
	| { step: 'form'; provider: Provider }
	| { step: 'summary'; provider: Provider };

const tabLabels: { key: Tab; label: string }[] = [
	{ key: 'analytics', label: 'Analytics' },
	{ key: 'connections', label: 'Connections' },
	{ key: 'routing', label: 'Routing' },
];

export default function CommunicationsPage() {
	const [tab, setTab] = useState<Tab>('analytics');

	/* ── Connections state ── */
	const [flow, setFlow] = useState<FlowStep>({ step: 'idle' });
	const [selConn, setSelConn] = useState<Connection | null>(null);
	const [connDialog, setConnDialog] = useState<'view' | 'edit' | 'test' | null>(
		null,
	);

	/* ── Routing state ── */
	const [routingRows, setRoutingRows] = useState(ROUTING_DATA);
	const [routingDetail, setRoutingDetail] = useState<RoutingConfig | null>(
		null,
	);
	const [setupCard, setSetupCard] = useState<{
		key: string;
		title: string;
		description: string;
	} | null>(null);
	const [toast, setToast] = useState<string | null>(null);

	/* ── Analytics state ── */
	const [timeWindow, setTimeWindow] = useState<TimeWindow>('7d');
	const [channel, setChannel] = useState<Channel>('All');
	const [showFilters, setShowFilters] = useState(false);

	const aggregate =
		AGGREGATE_SEED[channel]?.[timeWindow] ?? AGGREGATE_SEED.All['7d'];
	const bars = BAR_CHART_SEED[channel]?.[timeWindow] ?? [];

	const filteredConns =
		channel === 'All'
			? CONNECTIONS
			: CONNECTIONS.filter((c) => c.type === channel);
	const filteredRoutes =
		channel === 'All'
			? ROUTING_DATA
			: ROUTING_DATA.filter((r) => {
					if (channel === 'Email') return r.subdomain === 'Mail';
					if (channel === 'Push') return r.subdomain === 'Push';
					if (channel === 'Sms') return r.subdomain === 'SMS';
					return true;
				});
	const filteredRH =
		channel === 'All'
			? ROUTE_HEALTH
			: ROUTE_HEALTH.filter((r) => {
					if (channel === 'Email') return r.subdomain === 'Mail';
					if (channel === 'Push') return r.subdomain === 'Push';
					if (channel === 'Sms') return r.subdomain === 'SMS';
					return true;
				});

	const sendsScale =
		timeWindow === '24h'
			? 1 / 7
			: timeWindow === '48h'
				? 2 / 7
				: timeWindow === '30d'
					? 30 / 7
					: 1;
	const scaledRH = filteredRH.map((r) => ({
		...r,
		sends7d: Math.round(r.sends7d * sendsScale),
	}));

	const activeConns = filteredConns.filter((c) => c.status === 'Active').length;
	const errorConns = filteredConns.filter((c) => c.status === 'Error').length;
	const activeRoutes = filteredRoutes.filter(
		(r) => r.status === 'Active',
	).length;
	const activeClients = CLIENT_DATA.filter((c) => c.status === 'Active').length;

	const toggleRouting = useCallback((id: number) => {
		setRoutingRows((prev) => {
			const target = prev.find((r) => r.id === id);
			if (!target) return prev;
			if (target.status === 'Active') {
				const activeCount = prev.filter(
					(r) => r.status === 'Active' && r.subdomain === target.subdomain,
				).length;
				if (activeCount <= 1) {
					setToast(
						'This is the last active routing configuration for this subdomain. Please create and activate another configuration before making this one inactive.',
					);
					return prev;
				}
			}
			return prev.map((r) =>
				r.id === id
					? {
							...r,
							status:
								r.status === 'Active'
									? ('Inactive' as const)
									: ('Active' as const),
						}
					: r,
			);
		});
	}, []);

	const activeRoutingCount = routingRows.filter(
		(r) => r.status === 'Active',
	).length;
	const inactiveRoutingCount = routingRows.filter(
		(r) => r.status === 'Inactive',
	).length;

	return (
		<div className="flex flex-col">
			{/* Header */}
			<div className="border-b bg-card px-6 py-4">
				<h1 className="text-xl font-semibold">Communications</h1>
				<div className="mt-3 flex gap-1">
					{tabLabels.map((t) => (
						<button
							type="button"
							key={t.key}
							onClick={() => setTab(t.key)}
							className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
								tab === t.key
									? 'bg-background text-foreground shadow-sm'
									: 'text-muted-foreground hover:text-foreground'
							}`}
						>
							{t.label}
						</button>
					))}
				</div>
				<Breadcrumb className="mt-2">
					<BreadcrumbList>
						<BreadcrumbItem>
							<BreadcrumbLink href={Routes.CONNECTRA_ROOT}>
								Connectra
							</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<BreadcrumbLink>Communications</BreadcrumbLink>
						</BreadcrumbItem>
						<BreadcrumbSeparator />
						<BreadcrumbItem>
							<BreadcrumbLink>
								{tabLabels.find((t) => t.key === tab)?.label}
							</BreadcrumbLink>
						</BreadcrumbItem>
					</BreadcrumbList>
				</Breadcrumb>
			</div>

			{/* Content */}
			<div className="p-6">
				{tab === 'connections' && (
					<ConnectionsTable
						onAddNew={() => setFlow({ step: 'provider' })}
						onView={(c) => {
							setSelConn(c);
							setConnDialog('view');
						}}
						onEdit={(c) => {
							setSelConn(c);
							setConnDialog('edit');
						}}
						onTest={(c) => {
							setSelConn(c);
							setConnDialog('test');
						}}
					/>
				)}

				{tab === 'routing' && (
					<div className="space-y-6">
						<RoutingCards onSetup={(card) => setSetupCard(card)} />
						<RoutingTable
							rows={routingRows}
							onView={setRoutingDetail}
							onToggle={toggleRouting}
							onEdit={() => {}}
							activeCount={activeRoutingCount}
							inactiveCount={inactiveRoutingCount}
						/>
					</div>
				)}

				{tab === 'analytics' && (
					<div className="space-y-6">
						{/* Filters */}
						<div className="flex items-center gap-2">
							<Button
								variant={showFilters ? 'secondary' : 'outline'}
								size="sm"
								className="gap-1.5"
								onClick={() => setShowFilters((f) => !f)}
							>
								<SlidersHorizontal className="h-3.5 w-3.5" />
								{showFilters ? 'Hide Filters' : 'Show Filters'}
							</Button>
						</div>

						{showFilters && (
							<div className="flex items-end gap-3 rounded-lg border bg-muted/30 p-3">
								<div className="flex-1">
									<span className="mb-1 block text-xs text-muted-foreground">
										Time Range
									</span>
									<Select
										value={timeWindow}
										onValueChange={(v) => setTimeWindow(v as TimeWindow)}
									>
										<SelectTrigger className="h-9">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="24h">Last 24 Hours</SelectItem>
											<SelectItem value="48h">Last 48 Hours</SelectItem>
											<SelectItem value="7d">Last 7 Days</SelectItem>
											<SelectItem value="30d">Last 30 Days</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div className="flex-1">
									<span className="mb-1 block text-xs text-muted-foreground">
										Communication Type
									</span>
									<Select
										value={channel}
										onValueChange={(v) => setChannel(v as Channel)}
									>
										<SelectTrigger className="h-9">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="All">All</SelectItem>
											<SelectItem value="Email">Email</SelectItem>
											<SelectItem value="Push">Push</SelectItem>
											<SelectItem value="Sms">SMS</SelectItem>
										</SelectContent>
									</Select>
								</div>
								{(timeWindow !== '7d' || channel !== 'All') && (
									<Button
										variant="outline"
										size="sm"
										className="h-9"
										onClick={() => {
											setTimeWindow('7d');
											setChannel('All');
										}}
									>
										Clear
									</Button>
								)}
							</div>
						)}

						{/* KPIs */}
						<AnalyticsKPIs data={aggregate} timeWindow={timeWindow} />

						{/* Bar Chart */}
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">Connection Overview</CardTitle>
								<p className="text-xs text-muted-foreground">
									Bar length = total volume · Hover for details
								</p>
							</CardHeader>
							<CardContent>
								<BarChart bars={bars} />
							</CardContent>
						</Card>

						{/* System Summary */}
						<div className="grid grid-cols-4 gap-4">
							{[
								{
									label: 'Active Connections',
									value: activeConns,
									of: filteredConns.length,
									note: errorConns > 0 ? `${errorConns} error` : null,
									noteColor: 'text-red-600',
								},
								{
									label: 'Active Routes',
									value: activeRoutes,
									of: filteredRoutes.length,
									note: `${filteredRoutes.length - activeRoutes} inactive`,
									noteColor: 'text-muted-foreground',
								},
								{
									label: 'Active Clients',
									value: activeClients,
									of: CLIENT_DATA.length,
									note: null,
									noteColor: '',
								},
								{
									label: 'System Health',
									value: errorConns > 0 ? 'Warning' : 'Operational',
									of: null,
									note:
										errorConns > 0
											? `${errorConns} error`
											: 'All connections healthy',
									noteColor: errorConns > 0 ? 'text-red-600' : 'text-green-600',
								},
							].map((s) => (
								<div key={s.label} className="rounded-lg border bg-card p-4">
									<p className="text-xs text-muted-foreground">{s.label}</p>
									<p className="mt-1 text-lg font-semibold">
										{s.value}
										{s.of !== null && (
											<span className="text-sm font-normal text-muted-foreground">
												{' '}
												/ {s.of}
											</span>
										)}
									</p>
									{s.note && (
										<p className={`mt-0.5 text-xs ${s.noteColor}`}>{s.note}</p>
									)}
								</div>
							))}
						</div>

						{/* Route Health */}
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">Route Health</CardTitle>
								<p className="text-xs text-muted-foreground">
									Live status of all routing configurations
								</p>
							</CardHeader>
							<CardContent>
								<RouteHealthTable routes={scaledRH} />
							</CardContent>
						</Card>

						{/* Recent Activity */}
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">Recent Activity</CardTitle>
							</CardHeader>
							<CardContent>
								<RecentActivity items={RECENT_ACTIVITY} />
							</CardContent>
						</Card>
					</div>
				)}
			</div>

			{/* Dialogs */}
			{connDialog === 'view' && selConn && (
				<ConnViewDialog
					conn={selConn}
					open
					onClose={() => {
						setConnDialog(null);
						setSelConn(null);
					}}
				/>
			)}
			{connDialog === 'edit' && selConn && (
				<ConnEditDialog
					conn={selConn}
					open
					onClose={() => {
						setConnDialog(null);
						setSelConn(null);
					}}
				/>
			)}
			{connDialog === 'test' && selConn && (
				<ConnTestDialog
					conn={selConn}
					open
					onClose={() => {
						setConnDialog(null);
						setSelConn(null);
					}}
				/>
			)}

			<ProviderSelector
				open={flow.step === 'provider'}
				onSelect={(p) => setFlow({ step: 'form', provider: p })}
				onClose={() => setFlow({ step: 'idle' })}
			/>

			{flow.step === 'form' && (
				<ConnectionForm
					provider={flow.provider}
					onBack={() => setFlow({ step: 'provider' })}
					onNext={() => setFlow({ step: 'summary', provider: flow.provider })}
				/>
			)}

			{flow.step === 'summary' && (
				<ConnectionSummary
					provider={flow.provider}
					onBack={() => setFlow({ step: 'form', provider: flow.provider })}
					onClose={() => setFlow({ step: 'idle' })}
				/>
			)}

			{routingDetail && (
				<RoutingDetailDialog
					row={routingDetail}
					open
					onClose={() => setRoutingDetail(null)}
					onToggle={(id) => {
						toggleRouting(id);
						setRoutingDetail((prev) =>
							prev
								? {
										...prev,
										status: prev.status === 'Active' ? 'Inactive' : 'Active',
									}
								: null,
						);
					}}
				/>
			)}

			{setupCard && (
				<RoutingSetupDialog
					card={setupCard}
					open
					onClose={() => setSetupCard(null)}
				/>
			)}

			{/* Toast */}
			{toast && (
				<div className="fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border border-red-200 bg-red-50 p-4 shadow-lg">
					<div className="flex items-start gap-2">
						<span className="text-red-500">⚠</span>
						<div>
							<p className="text-sm font-medium text-red-800">
								Cannot deactivate
							</p>
							<p className="text-sm text-red-700">{toast}</p>
						</div>
						<button
							type="button"
							onClick={() => setToast(null)}
							className="ml-auto text-red-400 hover:text-red-600"
						>
							×
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
